import {
  infiniteQueryOptions,
  queryOptions,
  type InfiniteData,
  type QueryClient,
} from "@tanstack/react-query";
import type { WorkspaceSkillService } from "@/services/workspace-skill.service";
import type {
  TSkill,
  TUserSkillAssignment,
  TWorkspaceSkillsListQuery,
  TWorkspaceSkillsPage,
} from "@/types/skill";
import { getQueryClient } from "./client";
import { FRESHNESS } from "./policies";
import { scopedKey, scopedRead, scopeIfReady, type QueryScope } from "./scope";

/**
 * The workspace's skills and who holds them (NG-2802):
 *
 * - `skills.bank`: every skill, archived included, in the workspace's order,
 *   `[slug, uid, 'skills', 'bank']`. What the pickers, filters and labels read;
 *   each drops archived skills itself where it offers only live ones.
 * - `skills.users`: one skill's holders, `[slug, uid, 'skills', 'users', skillId]`.
 * - `skills.member`: one member's skills, `[slug, uid, 'skills', 'member', memberId]`.
 * - `skills.mine`: the signed-in member's skills, `[slug, uid, 'skills', 'mine']`.
 * - `skills.page`: the settings Skills table's pages (NG-2805), the same
 *   endpoint with the table's filters and sort, walked by cursor,
 *   `[slug, uid, 'skills', 'page', {q, user_ids, archived, order_by}]`.
 *   Mutable rather than reference: reused for 30 s, as the processes and
 *   templates tables are.
 *
 * Reference data: `WorkspaceSkillsStore` and `UserSkillsStore` cached them for
 * the whole session; as queries they are reused for 5 minutes, re-read by this
 * tab's writes, and by another tab's through the `skills:bank` hint. Every read
 * is a plain idempotent GET, so each declares `"safe-read"`.
 *
 * @writers `useSkillWriters` (`app/hooks/use-skills.ts`): create, import,
 * rename, archive, restore and delete a skill, and set a skill's holders, a
 * member's skills or the signed-in member's own. Each settles what it can with
 * the answer, puts an edited row into the table's cached pages in place, and
 * invalidates everything under `'skills'`, the table's pages included, once
 * the write is confirmed.
 */

/** The workspace's order: the admin-set sort order, then the name. */
export function sortSkills(skills: readonly TSkill[]): TSkill[] {
  return [...skills].sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name));
}

/** The `skills.bank` key. */
export function skillBankKey(scope: QueryScope) {
  return scopedKey(scope, "skills", "bank");
}

/** The `skills.users` key for one skill. */
export function skillUsersKey(scope: QueryScope, skillId: string) {
  return scopedKey(scope, "skills", "users", skillId);
}

/** The `skills.member` key for one member. */
export function memberSkillsKey(scope: QueryScope, memberId: string) {
  return scopedKey(scope, "skills", "member", memberId);
}

/** The `skills.mine` key. */
export function mySkillsKey(scope: QueryScope) {
  return scopedKey(scope, "skills", "mine");
}

/** `skills.bank`: walks every page of the list, archived skills included. */
export function skillBankQuery(
  service: Pick<WorkspaceSkillService, "fetchAll">,
  scope: QueryScope,
) {
  return queryOptions({
    ...FRESHNESS.reference,
    queryKey: skillBankKey(scope),
    queryFn: scopedRead(
      async (request) => sortSkills(await service.fetchAll(scope.tenant, {}, request)),
      { retry: "safe-read" },
    ),
  });
}

/** `skills.users`: every holder of one skill. */
export function skillUsersQuery(
  service: Pick<WorkspaceSkillService, "listSkillUsers">,
  scope: QueryScope,
  skillId: string,
) {
  return queryOptions({
    ...FRESHNESS.reference,
    queryKey: skillUsersKey(scope, skillId),
    queryFn: scopedRead((request) => service.listSkillUsers(scope.tenant, skillId, request), {
      retry: "safe-read",
    }),
  });
}

/** `skills.member`: one member's skill assignments. */
export function memberSkillsQuery(
  service: Pick<WorkspaceSkillService, "listMemberSkills">,
  scope: QueryScope,
  memberId: string,
) {
  return queryOptions({
    ...FRESHNESS.reference,
    queryKey: memberSkillsKey(scope, memberId),
    queryFn: scopedRead((request) => service.listMemberSkills(scope.tenant, memberId, request), {
      retry: "safe-read",
    }),
  });
}

/** `skills.mine`: the ids of the signed-in member's skills. */
export function mySkillsQuery(
  service: Pick<WorkspaceSkillService, "listMySkills">,
  scope: QueryScope,
) {
  return queryOptions({
    ...FRESHNESS.reference,
    queryKey: mySkillsKey(scope),
    queryFn: scopedRead(
      async (request) =>
        (await service.listMySkills(scope.tenant, request))
          .map((entry: TUserSkillAssignment) => entry.skill)
          .filter(Boolean),
      { retry: "safe-read" },
    ),
  });
}

/**
 * After a confirmed skill write: re-read every cached skill read in the
 * workspace, the table's pages included.
 *
 * A table read still in flight was sent before the write, and a re-read of a
 * page set with nothing cached yet (a first page under a new filter) would
 * join it rather than send its own and list what the write changed as it was
 * (NG-2562). Those reads are cancelled first, so the re-read asks again.
 */
export async function invalidateSkills(client: QueryClient, scope: QueryScope): Promise<void> {
  await client.cancelQueries({ queryKey: skillsPagesPrefix(scope) });
  await client.invalidateQueries({ queryKey: scopedKey(scope, "skills") });
}

/**
 * Put a write's answer into the cached bank at once, before the re-read: a
 * created skill is added, an edited one replaced (or patched), a deleted one
 * dropped. A bank not yet read is left alone.
 */
export function patchSkillBank(
  client: QueryClient,
  scope: QueryScope,
  change: { written: TSkill } | { patched: string; patch: Partial<TSkill> } | { deletedId: string },
): void {
  client.setQueryData<TSkill[]>(skillBankKey(scope), (bank) => {
    if (!bank) return bank;
    if ("deletedId" in change) return bank.filter((s) => s.id !== change.deletedId);
    if ("patched" in change) {
      return sortSkills(bank.map((s) => (s.id === change.patched ? { ...s, ...change.patch } : s)));
    }
    const rest = bank.filter((s) => s.id !== change.written.id);
    return sortSkills([...rest, change.written]);
  });
}

/** The table's server filters and sort: everything but the cursor and the page size. */
export type SkillsPageQuery = Omit<TWorkspaceSkillsListQuery, "cursor" | "per_page">;

/** Rows per table page, as the store asked. */
const TABLE_PER_PAGE = 50;

/**
 * The table key. Absent and empty parameters send the same request, so they
 * share an entry; `user_ids` is a set, so its order does not split one.
 */
function skillsPageKey(scope: QueryScope, query: SkillsPageQuery = {}) {
  return scopedKey(scope, "skills", "page", {
    q: query.q ?? "",
    user_ids: [...(query.user_ids ?? [])].sort(),
    archived: query.archived ?? null,
    order_by: query.order_by ?? "",
  });
}

/** Every cached table page in `scope`, whatever its filter. */
function skillsPagesPrefix(scope: QueryScope) {
  return scopedKey(scope, "skills", "page");
}

/**
 * `skills.page`: the settings table's pages. The first page asks with no
 * cursor; each later page asks with the cursor the previous one returned.
 */
export function skillsPageQuery(
  service: Pick<WorkspaceSkillService, "list">,
  scope: QueryScope,
  query: SkillsPageQuery = {},
) {
  return infiniteQueryOptions({
    ...FRESHNESS.mutable,
    queryKey: skillsPageKey(scope, query),
    initialPageParam: null as string | null,
    queryFn: scopedRead<TWorkspaceSkillsPage, string | null>(
      (request, { pageParam }) =>
        service.list(
          scope.tenant,
          { ...query, per_page: TABLE_PER_PAGE, ...(pageParam ? { cursor: pageParam } : {}) },
          request,
        ),
      { retry: "safe-read" },
    ),
    getNextPageParam: (last: TWorkspaceSkillsPage) =>
      last.next_page_results && last.next_cursor ? last.next_cursor : undefined,
  });
}

/**
 * Warm the table's first page for `query` from a route loader, so the table
 * answers from the cache on arrival. Rejects when the read fails, so the
 * loader can log it; the table shows its own error from the same entry.
 */
export async function prefetchSkillsPage(
  service: Pick<WorkspaceSkillService, "list">,
  slug: string,
  query: SkillsPageQuery = {},
): Promise<void> {
  const scope = scopeIfReady(slug, "skills page");
  if (!scope) return;
  await getQueryClient().fetchInfiniteQuery(skillsPageQuery(service, scope, query));
}

/** Every skill in the pages, once each: a row can reappear on the next page as rows are added. */
export function rowsOfSkillsPages(pages: readonly TWorkspaceSkillsPage[]): TSkill[] {
  const seen = new Set<string>();
  const rows: TSkill[] = [];
  for (const page of pages) {
    for (const skill of page.results) {
      if (seen.has(skill.id)) continue;
      seen.add(skill.id);
      rows.push(skill);
    }
  }
  return rows;
}

/**
 * A write's answer into one skill's row on every cached table page at once
 * (the edited skill, or a new holder count), so the row shows it before the
 * re-read the write's invalidation starts.
 */
export function patchSkillsPages(
  client: QueryClient,
  scope: QueryScope,
  skillId: string,
  patch: Partial<TSkill>,
): void {
  client.setQueriesData<InfiniteData<TWorkspaceSkillsPage>>(
    { queryKey: skillsPagesPrefix(scope) },
    (data) =>
      data && {
        ...data,
        pages: data.pages.map((page) => ({
          ...page,
          results: page.results.map((skill) =>
            skill.id === skillId ? { ...skill, ...patch } : skill,
          ),
        })),
      },
  );
}

/** A skill as the table's cached pages hold it, for a writer reading it before an edit. */
export function cachedSkillsPageRow(
  client: QueryClient,
  scope: QueryScope,
  skillId: string,
): TSkill | undefined {
  for (const [, data] of client.getQueriesData<InfiniteData<TWorkspaceSkillsPage>>({
    queryKey: skillsPagesPrefix(scope),
  })) {
    for (const page of data?.pages ?? []) {
      const skill = page.results.find((s) => s.id === skillId);
      if (skill) return skill;
    }
  }
  return undefined;
}
